import { clsx } from 'clsx'
import type { ReactNode } from 'react'
import { FormattedMessage } from 'react-intl'
import { View } from 'react-native'

import { Slider } from '@/src/elements/slider/Slider'
import { Typography } from '@/src/elements/text/Typography'
import { PublicationCard, type TPublicationCardProps } from '@/src/features/publication/ui/components/PublicationCard'
import { useDimensions } from '@/src/general/hooks/useDimensions'
import { useTranslate } from '@/src/lang/useTranslate'

import type { TGetSubscriptionPublication } from '../../../api/getSubscriptions.types'
import { getSliderClasses, JOURNEY_THUMBNAIL_WIDTH_CLASSNAME } from '../JourneySlider/JourneySliderBase'
import { EverlastingItemPlaceholder } from './EverlastingItemPlaceholder'

type TEverlastingItem = TPublicationCardProps['publication'] | TGetSubscriptionPublication

const renderPlaceholderItem = () => <EverlastingItemPlaceholder className={JOURNEY_THUMBNAIL_WIDTH_CLASSNAME} />

const getPlaceholderKey = (index: number) => `placeholder${index}`
const getItemKey = (item: TEverlastingItem) => item.id.toString()

const GridItem = ({ index, children }: { index: number; children: ReactNode }) => (
  <View className={clsx('max-w-[33%] w-full pb-2', index % 3 === 0 && 'pr-2', index % 3 === 2 && 'pl-2')}>
    {children}
  </View>
)

export type TEverlastingSliderBaseProps = {
  everlastingItems?: TEverlastingItem[]
} & Omit<TPublicationCardProps, 'publication' | 'className'>

export const EverlastingSliderBase = ({ everlastingItems, ...otherProps }: TEverlastingSliderBaseProps) => {
  const { t } = useTranslate()
  const { lessThan } = useDimensions()

  if (everlastingItems && everlastingItems.length === 0) return null

  const renderItem = (item: TEverlastingItem) =>
    item != null &&
    typeof item === 'object' &&
    'content' in item && (
      <PublicationCard
        className={clsx(
          'h-full bg-white',
          everlastingItems?.length === 1 || !lessThan.md ? 'w-full' : JOURNEY_THUMBNAIL_WIDTH_CLASSNAME,
        )}
        publication={item}
        {...otherProps}
      />
    )

  if (lessThan.md) {
    return (
      <Slider<TEverlastingItem>
        title={t('words.daily')}
        list={everlastingItems}
        getItemKey={getItemKey}
        renderItem={renderItem}
        renderPlaceholderItem={renderPlaceholderItem}
        classes={getSliderClasses(everlastingItems, {
          container: 'pl-4 pr-4',
          titleContainer: 'px-4',
        })}
      />
    )
  }

  return (
    <View className="py-4 flex flex-col gap-4 w-full px-4">
      <Typography heading="h2" size="xl" bold>
        <FormattedMessage id="words.daily" />
      </Typography>
      <View className={clsx('flex flex-row flex-wrap')}>
        {!everlastingItems &&
          Array.from({ length: 3 })
            .map((_, index) => ({ id: index }))
            .map((item) => (
              <GridItem index={item.id} key={getPlaceholderKey(item.id)}>
                {renderPlaceholderItem()}
              </GridItem>
            ))}
        {everlastingItems?.map((item, index) => {
          return (
            <GridItem index={index} key={getItemKey(item)}>
              {renderItem(item)}
            </GridItem>
          )
        })}
      </View>
    </View>
  )
}
